import React, { useState } from 'react';
import { ChatDashboard } from './ChatDashboard';
import { TraderDashboardContent } from './TraderDashboardContent';
import { BloombergRadioButton } from '../trading/BloombergRadioButton';
import { RolesInterface } from '../roles/RolesInterface';
import { Strategy, ExecutionTimingSettings } from '../trading/types';
import { useToast } from '@/components/ui/use-toast';
import { DataProviderSelectionDialog, DataProviderId } from './DataProviderSelectionDialog';
import { BrokerSelectionDialog, BrokerId } from './BrokerSelectionDialog';

interface MainDashboardProps {
  activeView: string;
  messages: any[];
  inputMessage: string;
  setInputMessage: (message: string) => void;
  isLoading: boolean;
  handleSendMessage: (e: React.FormEvent) => void;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  showRoles: boolean;
  setShowRoles: (show: boolean) => void;
  activeRole: any;
  setActiveRole: (role: any) => void;
  roles: any[];
  selectedStrategy: string;
  setSelectedStrategy: (strategy: string) => void;
  positions: any;
  strategies: any[];
  handleNewStrategy: (data: any) => void;
  handleDeleteStrategy?: (strategyId: string) => void; // Add optional deletion handler
  riskAlerts: any[];
}

export const MainDashboard = ({
  activeView,
  messages,
  inputMessage,
  setInputMessage,
  isLoading,
  handleSendMessage,
  isExpanded,
  setIsExpanded,
  showRoles,
  setShowRoles,
  activeRole,
  setActiveRole,
  roles,
  selectedStrategy,
  setSelectedStrategy,
  positions,
  strategies: propStrategies, 
  handleNewStrategy: propHandleNewStrategy,
  handleDeleteStrategy: propHandleDeleteStrategy, // Add to destructuring
  riskAlerts
}: MainDashboardProps) => {
  // Local state for strategies (if not provided via props)
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [isProviderSelectionOpen, setIsProviderSelectionOpen] = useState(false);
  const [selectedDataProvider, setSelectedDataProvider] = useState<DataProviderId | null>(null);
  const [isBrokerSelectionOpen, setIsBrokerSelectionOpen] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState<BrokerId | null>(null);
  const { toast } = useToast();

  // Use either prop strategies or local state
  const activeStrategies = propStrategies || strategies;

  // Default execution timing for newly created strategies
  const defaultExecutionTiming: ExecutionTimingSettings = {
    signalFrequency: 'daily_close',
    monitorFrequency: '5min',
    executeRthOnly: true,
    allowPrePostMarket: false,
  };

  // Function to transform a form strategy into a complete Strategy object
  const createStrategyFromForm = (formStrategy) => {
    return {
      id: formStrategy.id || `strategy-${Date.now()}`,
      name: formStrategy.name,
      description: formStrategy.description || '',
      engine: formStrategy.engine,
      riskLevel: formStrategy.riskLevel,
      symbols: formStrategy.symbols || [],
      useSentiment: formStrategy.useSentiment || false,
      executionTiming: formStrategy.executionTiming,
      riskParameters: formStrategy.riskParameters,
      backtestSettings: formStrategy.backtestSettings,
      isActive: true,
      // These are required by the Strategy interface
      models: [],
      exitConditions: [],
      baskets: []
    };
  };

  // Handle new strategy creation
  const handleNewStrategy = (formData) => {
    // If parent component provided a handler, use it
    if (propHandleNewStrategy) {
      return propHandleNewStrategy(formData);
    }
    
    // Otherwise use local implementation
    const newStrategy = createStrategyFromForm(formData);
    setStrategies(prev => [...prev, newStrategy]);
    setSelectedStrategy(newStrategy.id);
    
    toast({
      title: "Strategy Created",
      description: `Successfully created strategy: ${newStrategy.name}`,
    });
    
    return newStrategy;
  };
  
  // Data provider selection handlers
  const handleOpenProviderDialog = () => {
    setIsProviderSelectionOpen(true);
  };

  const handleCloseProviderDialog = () => {
    setIsProviderSelectionOpen(false);
  };

  const handleProviderSelected = (providerId: DataProviderId) => {
    setSelectedDataProvider(providerId);
    handleCloseProviderDialog();
    
    toast({
      title: "Data Provider Selected",
      description: `Selected ${providerId} as your data provider.`,
    });
  };
  
  // Broker selection handlers
  const handleOpenBrokerDialog = () => {
    setIsBrokerSelectionOpen(true);
  };

  const handleCloseBrokerDialog = () => {
    setIsBrokerSelectionOpen(false);
  };

  const handleBrokerSelected = (brokerId: BrokerId) => {
    setSelectedBroker(brokerId);
    handleCloseBrokerDialog();
    
    toast({
      title: "Broker Selected",
      description: `Selected ${brokerId} as your broker.`,
    });
  };

  // Sample data for positions (only when we have at least one strategy)
  const defaultPositions = {
    overview: {
      totalValue: 1250000,
      todayChange: 1.5,
      totalPositions: activeStrategies.length > 0 ? 3 : 0
    },
    allocation: [
      { type: 'US Equities', value: 45, amount: 562500 },
      { type: 'Fixed Income', value: 30, amount: 375000 },
      { type: 'Commodities', value: 15, amount: 187500 },
      { type: 'Crypto', value: 10, amount: 125000 }
    ]
  };

  // Use provided positions or default positions
  const displayPositions = positions || defaultPositions;

  const renderChatDashboard = () => (
    <>
      {showRoles && (
        <RolesInterface 
          roles={roles}
          activeRole={activeRole}
          setActiveRole={setActiveRole}
          showRoles={showRoles}
          setShowRoles={setShowRoles}
          setInputMessage={setInputMessage}
        />
      )}
      <ChatDashboard 
        messages={messages}
        inputMessage={inputMessage}
        setInputMessage={setInputMessage}
        isLoading={isLoading}
        handleSendMessage={handleSendMessage}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        onUploadDataClick={handleOpenProviderDialog}
        onConnectBrokerClick={handleOpenBrokerDialog}
        activeRole={activeRole}
      />
    </>
  );

  return (
    <div className="flex-1 p-6">
      {activeView === 'chat' ? (
        renderChatDashboard()
      ) : (
        <TraderDashboardContent
          messages={messages}
          inputMessage={inputMessage}
          setInputMessage={setInputMessage}
          isLoading={isLoading}
          handleSendMessage={handleSendMessage}
          isExpanded={isExpanded}
          setIsExpanded={setIsExpanded}
          showRoles={showRoles}
          setShowRoles={setShowRoles}
          activeRole={activeRole}
          setActiveRole={setActiveRole}
          roles={roles}
          selectedStrategy={selectedStrategy}
          setSelectedStrategy={setSelectedStrategy}
          activeStrategies={activeStrategies}
          displayPositions={displayPositions}
          handleNewStrategy={handleNewStrategy}
          handleDeleteStrategy={propHandleDeleteStrategy}
        />
      )}
      
      {/* Data Provider Selection Dialog */}
      <DataProviderSelectionDialog
        open={isProviderSelectionOpen}
        onOpenChange={handleCloseProviderDialog}
        onProviderSelect={handleProviderSelected}
      />
      
      {/* Broker Selection Dialog */}
      <BrokerSelectionDialog
        open={isBrokerSelectionOpen}
        onOpenChange={handleCloseBrokerDialog}
        onBrokerSelect={handleBrokerSelected}
      />
      
      {/* Bloomberg Radio Button */}
      <BloombergRadioButton />
    </div>
  );
};
